import { useState, useRef } from "react";
import { streamAI, api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Calculator, Send, Sparkles, Mic, Square, Upload, FileText, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";

export default function ValuationPage() {
  const { user } = useAuth();
  const [val, setVal] = useState({ business_name: "", industry: "Retail", country: "UAE", annual_revenue: 1000000, annual_profit: 200000, employees: 25, years_operating: 5, growth_rate: 20 });
  const [output, setOutput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [voiceNote, setVoiceNote] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = e => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const { data } = await api.post("/ai/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setVoiceNote(data.text);
          toast.success("Voice note transcribed");
        } catch { toast.error("Transcription failed"); }
        setTranscribing(false);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch { toast.error("Microphone access denied"); }
  };

  const stopRec = () => { mediaRef.current?.stop(); setRecording(false); };

  const onFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles(prev => [...prev, data]);
      } catch { toast.error(`Upload failed: ${f.name}`); }
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeFile = (id) => setFiles(prev => prev.filter(f => f.id !== id));

  const run = async (e) => {
    e.preventDefault();
    setOutput(""); setStreaming(true);
    let extra = "";
    if (voiceNote) extra += `\n\nVoice note from owner: ${voiceNote}`;
    if (files.length) extra += `\n\nUploaded ${files.length} document(s): ${files.map(f => f.filename).join(", ")}`;
    const desc = val.business_name + extra;
    await streamAI("/ai/valuation", {
      ...val,
      business_name: desc,
      annual_revenue: Number(val.annual_revenue),
      annual_profit: Number(val.annual_profit),
      employees: Number(val.employees),
      years_operating: Number(val.years_operating),
      growth_rate: Number(val.growth_rate),
    }, t => setOutput(p => p + t), () => setStreaming(false), () => setStreaming(false));
  };

  return (
    <div data-testid="valuation-page" className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
      <Seo
        title="Free Business Valuation — Voice, Photos or Form | DealVest"
        description="Get a free AI-powered business valuation in 60 seconds. Speak to AI, upload receipts and documents, or fill a simple form. Built for any business — from farms to factories."
        path="/valuation"
      />
      <div className="text-center max-w-2xl mx-auto mb-10">
        <span className="dv-pill mb-4"><Sparkles size={13} /> Free · No expertise needed</span>
        <h1 className="dv-display text-4xl md:text-5xl mb-3">What is your business worth?</h1>
        <p className="text-lg text-slate-600">Speak, upload documents, or fill the form — we'll do the rest.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
        <form onSubmit={run} className="bg-white rounded-2xl border border-slate-200 p-6 md:p-7 dv-shadow-soft" data-testid="valuation-form">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center"><Calculator size={20} className="text-blue-600" /></div>
            <h3 className="font-bold text-xl">Tell us about your business</h3>
          </div>

          {/* Voice note */}
          <div className="bg-blue-50 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div>
                <p className="text-sm font-semibold text-slate-800">Voice note (optional)</p>
                <p className="text-xs text-slate-500">Describe your operations, customers, suppliers</p>
              </div>
              {!recording ? (
                <button type="button" onClick={startRec} disabled={transcribing} className="dv-btn dv-btn-primary dv-btn-sm" data-testid="val-record-btn">
                  <Mic size={14} /> {transcribing ? "..." : "Record"}
                </button>
              ) : (
                <button type="button" onClick={stopRec} className="dv-btn dv-btn-sm bg-red-600 hover:bg-red-700 text-white dv-pulse-blue" data-testid="val-stop-btn">
                  <Square size={12} /> Stop
                </button>
              )}
            </div>
            {voiceNote && <p className="text-xs text-slate-700 italic mt-2 bg-white rounded p-2" data-testid="voice-note-text">"{voiceNote}"</p>}
          </div>

          {/* Documents */}
          <div className="bg-slate-50 rounded-xl p-4 mb-5">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <p className="text-sm font-semibold text-slate-800">Documents (optional)</p>
                <p className="text-xs text-slate-500">Receipts, bank statements, invoices, photos</p>
              </div>
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading || !user} className="dv-btn dv-btn-secondary dv-btn-sm" data-testid="upload-btn" title={user ? "" : "Login required to upload"}>
                <Upload size={14} /> {uploading ? "..." : "Add"}
              </button>
              <input ref={fileInputRef} type="file" multiple accept="image/*,application/pdf,.csv,.xlsx" onChange={onFiles} className="hidden" />
            </div>
            {!user && <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1.5 mb-2">Log in to upload documents.</p>}
            {files.length > 0 && (
              <div className="space-y-1.5" data-testid="uploaded-files">
                {files.map(f => (
                  <div key={f.id} className="flex items-center gap-2 bg-white rounded p-2 border border-slate-200">
                    {f.content_type?.startsWith("image/") ? <ImageIcon size={14} className="text-blue-600" /> : <FileText size={14} className="text-blue-600" />}
                    <span className="text-xs text-slate-700 flex-1 truncate">{f.filename}</span>
                    <button type="button" onClick={() => removeFile(f.id)} className="text-slate-400 hover:text-red-500"><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div><label className="dv-label">Business name</label>
              <input className="dv-input" placeholder="Acme Logistics" required value={val.business_name} onChange={e => setVal({ ...val, business_name: e.target.value })} data-testid="val-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="dv-label">Industry</label><input className="dv-input" value={val.industry} onChange={e => setVal({ ...val, industry: e.target.value })} /></div>
              <div><label className="dv-label">Country</label><input className="dv-input" value={val.country} onChange={e => setVal({ ...val, country: e.target.value })} /></div>
              <div><label className="dv-label">Revenue (USD/yr)</label><input type="number" className="dv-input" value={val.annual_revenue} onChange={e => setVal({ ...val, annual_revenue: e.target.value })} /></div>
              <div><label className="dv-label">Profit (USD/yr)</label><input type="number" className="dv-input" value={val.annual_profit} onChange={e => setVal({ ...val, annual_profit: e.target.value })} /></div>
              <div><label className="dv-label">Employees</label><input type="number" className="dv-input" value={val.employees} onChange={e => setVal({ ...val, employees: e.target.value })} /></div>
              <div><label className="dv-label">Years operating</label><input type="number" className="dv-input" value={val.years_operating} onChange={e => setVal({ ...val, years_operating: e.target.value })} /></div>
              <div className="col-span-2"><label className="dv-label">YoY growth (%)</label><input type="number" className="dv-input" value={val.growth_rate} onChange={e => setVal({ ...val, growth_rate: e.target.value })} /></div>
            </div>
          </div>
          <button type="submit" disabled={streaming} className="dv-btn dv-btn-primary w-full mt-5" data-testid="val-run-btn">
            <Send size={16} /> {streaming ? "Analyzing..." : "Get my valuation"}
          </button>
        </form>

        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6 md:p-7 min-h-[400px]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-xl flex items-center gap-2"><Sparkles size={18} className="text-blue-600" /> AI Valuation Report</h3>
            {streaming && <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">Streaming</span>}
          </div>
          <div className="text-[15px] text-slate-700 whitespace-pre-wrap leading-relaxed font-mono max-h-[640px] overflow-y-auto" data-testid="val-output">
            {output || <span className="text-slate-400 font-sans">Submit the form to receive your valuation report.</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
